import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, select } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { getLoggedInUser, updateUser, updateUserFail } from 'ish-core/store/customer/user';

import { accountNavigationBlocksActions } from './account-navigation-blocks.actions';
import { getAccountNavigationBlocks } from './account-navigation-blocks.selectors';

/**
 * The navigation cards the user created are stored as a custom attribute of the user profile, so they
 * arrive with the user and are written back through the existing user update. Like the favorites the
 * feature has no own resource and no own request: loading is a pure projection of the profile that is
 * already in the store.
 */
@Injectable()
export class AccountNavigationBlocksEffects {
  constructor(private actions$: Actions, private store: Store) {}

  /**
   * Takes over the cards whenever the logged in user changes, on login, on a profile update and after
   * a page reload that restores the session.
   */
  syncAccountNavigationBlocks$ = createEffect(() =>
    this.store.pipe(
      select(getLoggedInUser),
      map(user => user?.navigationBlocks ?? []),
      distinctUntilChanged(isEqual),
      map(blocks => accountNavigationBlocksActions.syncAccountNavigationBlocks({ blocks }))
    )
  );

  /**
   * Saves the changed cards with the profile of the user. The reducer has already applied the change,
   * so the current state is what is sent. No success message is passed on, the card that appears or
   * disappears is the feedback.
   */
  saveAccountNavigationBlocks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        accountNavigationBlocksActions.addAccountNavigationBlock,
        accountNavigationBlocksActions.updateAccountNavigationBlock,
        accountNavigationBlocksActions.removeAccountNavigationBlock
      ),
      concatLatestFrom(() => [
        this.store.pipe(select(getAccountNavigationBlocks)),
        this.store.pipe(select(getLoggedInUser)),
      ]),
      filter(([, , user]) => !!user),
      map(([, navigationBlocks, user]) => updateUser({ user: { ...user, navigationBlocks } }))
    )
  );

  /**
   * A rejected profile update leaves the user in the store untouched, so the optimistic change is
   * rolled back by taking the cards of that profile over again.
   */
  rollbackAccountNavigationBlocks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserFail),
      concatLatestFrom(() => this.store.pipe(select(getLoggedInUser))),
      map(([, user]) =>
        accountNavigationBlocksActions.syncAccountNavigationBlocks({ blocks: user?.navigationBlocks ?? [] })
      )
    )
  );
}
